import { useEffect, useRef, useState } from "react";
import { CaretDown, Desktop, Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "next-themes";

const options = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Desktop },
];

export default function PublicThemeControl({ compact = false }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const selected = mounted ? theme || "system" : "system";
  const active = options.find((option) => option.value === selected) || options[2];
  const ActiveIcon = active.icon;

  const focusMenuItem = (index) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const normalized = (index + items.length) % items.length;
    items[normalized]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    setOpen(true);
    window.requestAnimationFrame(() => {
      const selectedIndex = Math.max(0, options.findIndex((option) => option.value === selected));
      focusMenuItem(selectedIndex);
    });
  };

  const handleMenuKeyDown = (event) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const currentIndex = Math.max(0, items.indexOf(document.activeElement));

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusMenuItem(currentIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusMenuItem(currentIndex - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusMenuItem(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusMenuItem(items.length - 1);
    }
  };

  return (
    <div className={`public-theme-control${compact ? " is-compact" : ""}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="public-theme-control__trigger"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Appearance: ${active.label}`}
      >
        <ActiveIcon size={16} />
        {!compact && <span>{active.label}</span>}
        <CaretDown size={13} className={open ? "is-open" : ""} />
      </button>

      {open && (
        <div ref={menuRef} className="public-theme-control__menu" role="menu" aria-label="Appearance" onKeyDown={handleMenuKeyDown}>
          {options.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={selected === value}
              tabIndex={selected === value ? 0 : -1}
              className={selected === value ? "is-selected" : ""}
              onClick={() => {
                setTheme(value);
                if (typeof window !== "undefined") {
                  try {
                    window.localStorage.removeItem("callsect-theme");
                    if (value === "system") window.localStorage.removeItem("callsect-public-theme");
                  } catch {
                    // Keep the live theme selection even when storage is unavailable.
                  }
                }
                setOpen(false);
                window.requestAnimationFrame(() => triggerRef.current?.focus());
              }}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
